import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import {
  Cloud,
  CloudRain,
  Sun,
  Wind,
  Thermometer,
  Sparkles,
  CloudSun,
  Snowflake,
  AlertTriangle,
  CheckCircle2,
  CircleAlert,
} from "lucide-react";
import api from "@/lib/api";
import ProUpsell from "@/components/ProUpsell";
import DaylightArc from "@/components/DaylightArc";
import { useAuth } from "@/context/AuthContext";
import { formatTemp } from "@/lib/format";

/**
 * Weather-aware inspection forecast tab.
 *
 * Shows a 5-day inspection rating for the hive's location. Each day is
 * coloured by suitability for working bees:
 *   - green  = great
 *   - amber  = ok
 *   - red    = avoid
 */
export default function HiveWeatherTab({ hiveId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upsell, setUpsell] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.get(`/weather/hive/${hiveId}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (e.response?.status === 402) {
          if (!cancelled) setUpsell(true);
        } else if (e.response?.status === 503) {
          if (!cancelled) setError("Weather service isn't configured on the server.");
        } else if (!cancelled) {
          setError(e.response?.data?.detail || "Couldn't fetch the forecast.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [hiveId]);

  if (loading) return <div className="text-sm text-stone-500 py-6" data-testid="weather-loading">Loading forecast…</div>;
  if (upsell) {
    return (
      <div data-testid="weather-upsell">
        <ProUpsell open={upsell} onOpenChange={setUpsell} feature="weather" />
        <div className="text-center py-12 border border-dashed border-stone-300 rounded-2xl">
          <Sparkles className="w-6 h-6 text-amber-500 mx-auto mb-2" />
          <p className="text-stone-600 text-sm">Inspection Forecast is a Pro feature.</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-center py-12 border border-dashed border-stone-300 rounded-2xl" data-testid="weather-error">
        <AlertTriangle className="w-6 h-6 text-stone-400 mx-auto mb-2" />
        <p className="text-stone-600 text-sm">{error}</p>
      </div>
    );
  }
  if (!data?.available) {
    return (
      <div className="text-center py-12 border border-dashed border-stone-300 rounded-2xl" data-testid="weather-no-location">
        <CloudSun className="w-6 h-6 text-stone-400 mx-auto mb-2" />
        <p className="text-stone-600 text-sm max-w-md mx-auto">
          {data?.reason || "Set this hive's Location to a recognisable city or address to enable the forecast."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="weather-tab">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display text-lg font-medium flex items-center gap-2">
            <CloudSun className="w-5 h-5 text-sky-600" />
            Inspection forecast
          </h3>
          <p className="text-xs text-stone-500 mt-0.5">
            {data.location_label}{" "}
            <span className="text-stone-400">
              · {data.latitude.toFixed(2)}, {data.longitude.toFixed(2)}
            </span>
          </p>
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-stone-500">
          Updated {format(parseISO(data.fetched_at), "HH:mm")}
        </div>
      </header>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-1">
          <DaylightArc
            sunrise={data.sunrise}
            sunset={data.sunset}
            tzOffset={data.timezone_offset}
            temp_max_f={data.days?.[0]?.temp_max_f}
            temp_min_f={data.days?.[0]?.temp_min_f}
          />
        </div>
        <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="weather-days">
          {data.days.slice(0, 5).map((d) => (
            <DayCard key={d.date} day={d} />
          ))}
        </div>
      </div>

      <Legend />
    </div>
  );
}

function DayCard({ day }) {
  const { user: prefs } = useAuth();
  const unit = prefs?.temperature_unit || "F";
  const tone = {
    great: "border-emerald-300 bg-emerald-50/60 ring-emerald-200",
    ok: "border-amber-300 bg-amber-50/60 ring-amber-200",
    avoid: "border-rose-300 bg-rose-50/60 ring-rose-200",
  }[day.rating];

  const Pill = {
    great: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />,
    ok: <CircleAlert className="w-3.5 h-3.5 text-amber-700" />,
    avoid: <AlertTriangle className="w-3.5 h-3.5 text-rose-700" />,
  }[day.rating];

  const pillBg = {
    great: "bg-emerald-100 text-emerald-900 border-emerald-200",
    ok: "bg-amber-100 text-amber-900 border-amber-200",
    avoid: "bg-rose-100 text-rose-900 border-rose-200",
  }[day.rating];

  const ConditionIcon = pickIcon(day.summary, day.has_rain);

  let dateLabel;
  try { dateLabel = format(parseISO(day.date), "EEE, MMM d"); }
  catch { dateLabel = day.date; }

  return (
    <div className={`border ${tone} ring-1 rounded-2xl p-4`} data-testid={`weather-day-${day.date}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-700">{dateLabel}</div>
        <span className={`text-[10px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded-full border ${pillBg} inline-flex items-center gap-1`}>
          {Pill} {day.rating_label}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <ConditionIcon className="w-7 h-7 text-stone-700" />
        <div>
          <div className="font-display text-2xl font-bold text-stone-900 tabular-nums leading-none">{formatTemp(day.temp_max_f, prefs, { noUnit: true })}°<span className="text-sm font-normal text-stone-500">{unit}</span></div>
          <div className="text-xs text-stone-500">low {formatTemp(day.temp_min_f, prefs, { noUnit: true })}°</div>
        </div>
      </div>
      <div className="text-xs text-stone-600 mt-2 capitalize">{day.summary}</div>
      <div className="flex items-center gap-3 text-[11px] text-stone-500 mt-2">
        <span className="inline-flex items-center gap-1"><Wind className="w-3 h-3" /> {day.wind_max_mph} mph</span>
        <span className="inline-flex items-center gap-1"><CloudRain className="w-3 h-3" /> {day.precip_chance}%</span>
      </div>
      <div className="text-[11px] text-stone-700 mt-2 italic">{day.reason}</div>
    </div>
  );
}

function Legend() {
  return (
    <div className="text-xs text-stone-500 flex flex-wrap gap-3 pt-2">
      <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" /> Great — 60–85°F, calm, clear</span>
      <span className="inline-flex items-center gap-1"><CircleAlert className="w-3.5 h-3.5 text-amber-700" /> OK — workable</span>
      <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-rose-700" /> Avoid — rain, too cold/hot, or windy</span>
    </div>
  );
}

function pickIcon(summary = "", hasRain = false) {
  const s = summary.toLowerCase();
  if (hasRain || s.includes("rain") || s.includes("drizzle") || s.includes("thunder")) return CloudRain;
  if (s.includes("snow")) return Snowflake;
  if (s.includes("clear")) return Sun;
  if (s.includes("cloud")) return Cloud;
  if (s.includes("cold")) return Thermometer;
  return CloudSun;
}
